import type { Paginated, SessionDetailDto, SessionDto, WarehouseDto } from '@bnn/shared';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { Alert, Badge, Button, Card, Empty, Field, Input, Modal, PageHeader, Pagination, Select, Table } from '../components/ui';
import { api, qs } from '../lib/api';
import { dt, STATUS_LABEL, STATUS_TONE } from '../lib/format';
import { errMsg, useApi } from '../lib/useApi';

export default function SessionsPage() {
  const { can } = useAuth();
  const nav = useNavigate();
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const list = useApi<Paginated<SessionDto>>(`/sessions${qs({ status, page, page_size: 30 })}`);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <PageHeader
        title="รอบการนับ"
        subtitle="1 รอบ = 1 คลัง · คลังเดียวกันเปิดนับได้ทีละ 1 รอบ"
        actions={
          can('sessions.manage') && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              + สร้างรอบการนับ
            </Button>
          )
        }
      />
      <Card>
        <div className="mb-3 flex flex-wrap gap-2">
          {['', 'OPEN', 'REVIEW', 'COMPLETED', 'CANCELLED'].map((s) => (
            <Button
              key={s}
              size="sm"
              variant={status === s ? 'primary' : 'secondary'}
              onClick={() => {
                setStatus(s);
                setPage(1);
              }}
            >
              {s ? STATUS_LABEL[s as SessionDto['status']] : 'ทั้งหมด'}
            </Button>
          ))}
        </div>
        {list.error && <Alert>{list.error.message}</Alert>}
        {list.data && list.data.total === 0 ? (
          <Empty>ยังไม่มีรอบการนับ</Empty>
        ) : (
          list.data && (
            <>
              <Table head={['รอบ', 'คลัง', 'สถานะ', 'รายการ', 'สร้างโดย', '']} minWidth={680}>
                {list.data.data.map((s) => (
                  <tr key={s.id}>
                    <td className="font-semibold whitespace-nowrap">
                      <Link to={`/sessions/${s.id}`} className="text-brand hover:underline">
                        {s.session_code}
                      </Link>
                    </td>
                    <td>
                      <b>{s.warehouse_code}</b> <span className="text-muted">{s.warehouse_name}</span>
                    </td>
                    <td>
                      <Badge tone={STATUS_TONE[s.status]}>{STATUS_LABEL[s.status]}</Badge>
                    </td>
                    <td className="whitespace-nowrap">
                      {s.item_count.toLocaleString()} สินค้า · {s.entry_count.toLocaleString()} ครั้ง
                    </td>
                    <td className="text-xs text-muted">
                      {s.created_by.display_name}
                      <div>{dt(s.created_at)}</div>
                    </td>
                    <td className="text-right whitespace-nowrap">
                      {s.status === 'OPEN' && can('count.scan') && (
                        <Button size="sm" variant="primary" onClick={() => nav(`/count/${s.id}`)}>
                          นับ
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </Table>
              <Pagination page={list.data.page} pageSize={list.data.page_size} total={list.data.total} onPage={setPage} />
            </>
          )
        )}
      </Card>
      <Modal open={creating} title="สร้างรอบการนับ" onClose={() => setCreating(false)}>
        <CreateSession onDone={(s) => nav(`/sessions/${s.id}`)} />
      </Modal>
    </>
  );
}

function CreateSession({ onDone }: { onDone: (s: SessionDetailDto) => void }) {
  const whs = useApi<WarehouseDto[]>('/warehouses');
  const [code, setCode] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const usable = whs.data?.filter((w) => !w.is_locked) ?? [];

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!code) return setError('กรุณาเลือกคลัง');
    setBusy(true);
    setError('');
    try {
      onDone(await api.post<SessionDetailDto>('/sessions', { warehouse_code: code, note: note || undefined }));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      {error && <Alert>{error}</Alert>}
      <Field label="คลัง" hint={whs.data && usable.length < whs.data.length ? 'คลังที่ถูกล็อกไม่แสดงในรายการ' : undefined}>
        <Select value={code} onChange={(e) => setCode(e.target.value)} autoFocus>
          <option value="">— เลือกคลัง —</option>
          {usable.map((w) => (
            <option key={w.whs_code} value={w.whs_code}>
              {w.whs_code} {w.whs_name}
            </option>
          ))}
        </Select>
      </Field>
      {whs.data?.length === 0 && (
        <Alert tone="warn">
          ยังไม่มีคลัง — เพิ่มที่หน้า <Link to="/warehouses" className="underline">คลังสินค้า</Link>
        </Alert>
      )}
      <Field label="หมายเหตุ (ไม่บังคับ)">
        <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="เช่น นับสิ้นเดือน พ.ย." />
      </Field>
      <Button type="submit" variant="primary" className="w-full" loading={busy}>
        สร้างและเปิดนับ
      </Button>
    </form>
  );
}
